import { Rng } from '../rng';
import type { InstrumentId } from '../instruments/types';
import type { SessionFacts } from './index';

/**
 * Daily quests and the monthly challenge (plan doc 13).
 *
 * The catalog is content; this is the machinery. Two things matter here and
 * both are why it is pure: the selection has to be *the same* on the client and
 * the server for a given user and day, or a learner sees three quests and the
 * server credits three others; and the progress a session makes has to be
 * computed the same way in both places, or a quest completes on screen and not
 * in the database.
 */

/** What a quest counts. Each maps to something a finished session reports. */
export type QuestMetric =
  | 'xp'
  | 'lessons'
  | 'comboBest'
  | 'earExercises'
  | 'micPasses'
  | 'songsPlayed'
  | 'songTwoStars'
  | 'mistakesSessions'
  | 'perfectLessons'
  | 'studioMinutes'
  | 'warmUps'
  | 'secondInstrument'
  | 'legendary'
  | 'skillsReviewed'
  | 'xpBeforeNoon';

export type QuestDifficulty = 'easy' | 'medium' | 'hard';

export const QUEST_GEMS: Readonly<Record<QuestDifficulty, number>> = {
  easy: 10,
  medium: 15,
  hard: 20,
};

/** All three in a day pays a bonus on top. */
export const ALL_QUESTS_BONUS_GEMS = 20;

export interface QuestDef {
  id: string;
  /** Imperative, with `{n}` where the target goes. */
  title: string;
  metric: QuestMetric;
  /** Target at easy, medium and hard, before the section scaling. */
  targets: readonly [number, number, number];
  /** Needs an instrument; at least one of the day's three is one of these. */
  instrument?: boolean;
  /** Section the learner must have reached for this quest to be offered. */
  minSection?: number;
  /** Only for these instruments, when it is instrument-specific. */
  only?: readonly InstrumentId[];
}

/**
 * How much bigger the targets get deeper into the course.
 *
 * The plan says targets scale by section reached and gives three numbers per
 * quest; the three numbers are the difficulties, and this is the scaling. It is
 * deliberately mild — a hard quest should still be one good sitting, not an
 * evening — and it rounds to something a person would say out loud.
 */
export function sectionScale(sectionReached: number): number {
  if (sectionReached <= 3) return 1;
  if (sectionReached <= 6) return 1.25;
  return 1.5;
}

/** The target for a quest at a difficulty, for a learner this far in. */
export function questTarget(
  quest: QuestDef,
  difficulty: QuestDifficulty,
  sectionReached: number,
): number {
  const index = difficulty === 'easy' ? 0 : difficulty === 'medium' ? 1 : 2;
  const base = quest.targets[index] ?? quest.targets[0];
  const scaled = base * sectionScale(sectionReached);
  // Round to a tidy number: fives above ten, whole numbers below.
  return scaled >= 10 ? Math.round(scaled / 5) * 5 : Math.max(1, Math.round(scaled));
}

/** One of the day's three quests, as issued. */
export interface IssuedQuest {
  questId: string;
  difficulty: QuestDifficulty;
  target: number;
  gems: number;
}

export interface QuestSelection {
  /** `YYYY-MM-DD` in the learner's local time. */
  periodKey: string;
  quests: IssuedQuest[];
}

export interface SelectQuestsOptions {
  catalog: readonly QuestDef[];
  /** The learner's local date, which is also the period key. */
  localDate: string;
  /** Stable per learner, so two people get different quests on the same day. */
  userSeed: number;
  sectionReached: number;
  instrument: InstrumentId | null;
  /** Yesterday's quest ids, which this set avoids repeating. */
  yesterday?: readonly string[];
}

/**
 * The three quests for a day.
 *
 * Seeded by the learner and the date, so it is the same every time it is asked
 * for — the client shows them before the server has ever heard of the day, and
 * the two must agree.
 */
/**
 * Metrics that *every* finished lesson moves.
 *
 * Deliberately narrow. `earExercises` and `skillsReviewed` only move when the
 * lesson happened to contain those, `perfectLessons` only on a clean run, and
 * `xpBeforeNoon` only in the morning — so none of them can carry the guarantee
 * that a day's quests respond to ordinary work. XP, a finished lesson and a
 * combo always do.
 */
export const LESSON_METRICS: readonly QuestMetric[] = ['xp', 'lessons', 'comboBest'];

export function isLessonMetric(metric: QuestMetric): boolean {
  return LESSON_METRICS.includes(metric);
}

export function selectDailyQuests(options: SelectQuestsOptions): QuestSelection {
  const { catalog, localDate, userSeed, sectionReached, instrument } = options;
  const rng = new Rng(dateSeed(localDate) ^ userSeed);
  const yesterday = new Set(options.yesterday ?? []);

  const eligible = catalog.filter((quest) => {
    if ((quest.minSection ?? 1) > sectionReached) return false;
    if (quest.instrument === true && instrument === null) return false;
    if (quest.only !== undefined && (instrument === null || !quest.only.includes(instrument))) {
      return false;
    }
    return true;
  });

  // Yesterday's quests are avoided, but never at the cost of having three: a
  // small catalog early on would otherwise leave a learner short.
  const fresh = eligible.filter((quest) => !yesterday.has(quest.id));
  const pool = fresh.length >= 3 ? fresh : eligible;

  const difficulties: QuestDifficulty[] = ['easy', 'medium', 'hard'];
  const chosen: QuestDef[] = [];
  const shuffled = rng.shuffle(pool);

  // At least one instrument quest when there is an instrument to play.
  if (instrument !== null) {
    const first = shuffled.find((quest) => quest.instrument === true);
    if (first !== undefined) chosen.push(first);
  }

  // And at least one that finishing a lesson moves. Without this the shuffle
  // can hand out three quests that all need something else — an instrument, a
  // studio session, a mistakes drill — and a learner who spends an hour on
  // lessons watches none of them budge.
  const fromLessons = shuffled.find((quest) => isLessonMetric(quest.metric));
  if (fromLessons !== undefined && !chosen.includes(fromLessons)) chosen.push(fromLessons);

  for (const quest of shuffled) {
    if (chosen.length >= 3) break;
    if (!chosen.includes(quest)) chosen.push(quest);
  }

  return {
    periodKey: localDate,
    // Sorted by id so the order is stable whichever way they were picked.
    quests: chosen
      .map((quest, i) => {
        const difficulty = difficulties[i] ?? 'easy';
        return {
          questId: quest.id,
          difficulty,
          target: questTarget(quest, difficulty, sectionReached),
          gems: QUEST_GEMS[difficulty],
        };
      })
      .sort((a, b) => a.questId.localeCompare(b.questId)),
  };
}

/** A stable number from a `YYYY-MM-DD` date. */
export function dateSeed(localDate: string): number {
  let h = 2166136261;
  for (const ch of localDate) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/* ------------------------------------------------------------- progress */

/** Everything a finished session contributes, beyond the facts XP needs. */
export interface QuestFacts extends SessionFacts {
  /** XP the session actually earned, after caps and boosts. */
  xpEarned: number;
  /** Local clock hour the session ended at, 0–23. */
  endedHour: number;
  /** Ear-training questions answered, right or wrong. */
  earExercises?: number;
  /** Skills whose review was due and got asked. */
  skillsReviewed?: number;
  /** Minutes the session lasted. */
  minutes?: number;
  /** The studio tool, when this was a studio session. */
  studioTool?: string;
  /** True when the session was played on a non-primary instrument. */
  onSecondInstrument?: boolean;
  /** Practice mode, for the mistakes quest. */
  practiceMode?: string;
}

/**
 * How much a session moves each metric.
 *
 * `comboBest` is a high-water mark rather than a sum, which the caller has to
 * honor: everything else adds up, that one takes the maximum.
 */
export function questProgress(facts: QuestFacts): Partial<Record<QuestMetric, number>> {
  const isLesson = facts.kind === 'lesson' || facts.kind === 'checkpoint';
  const out: Partial<Record<QuestMetric, number>> = {};

  const add = (metric: QuestMetric, amount: number): void => {
    if (amount > 0) out[metric] = (out[metric] ?? 0) + amount;
  };

  add('xp', facts.xpEarned);
  if (facts.endedHour < 12) add('xpBeforeNoon', facts.xpEarned);
  if (isLesson) add('lessons', 1);
  if (isLesson && facts.perfect) add('perfectLessons', 1);
  if (isLesson && facts.legendary === true) add('legendary', 1);
  add('micPasses', facts.micPasses ?? 0);
  add('earExercises', facts.earExercises ?? 0);
  add('skillsReviewed', facts.skillsReviewed ?? 0);
  if (facts.kind === 'song') {
    add('songsPlayed', 1);
    if ((facts.stars ?? 0) >= 2) add('songTwoStars', 1);
  }
  if (facts.kind === 'studio') {
    add('studioMinutes', Math.max(0, Math.round(facts.minutes ?? 0)));
    if (facts.studioTool === 'warm-up') add('warmUps', 1);
  }
  if (facts.practiceMode === 'mistakes') add('mistakesSessions', 1);
  if (facts.onSecondInstrument === true) add('secondInstrument', 1);

  // The high-water mark, not a sum.
  if (facts.maxCombo > 0) out.comboBest = facts.maxCombo;
  return out;
}

/** Metrics that take the best value rather than adding up. */
export const HIGH_WATER_METRICS: readonly QuestMetric[] = ['comboBest'];

export function isHighWater(metric: QuestMetric): boolean {
  return HIGH_WATER_METRICS.includes(metric);
}

/** A quest's state after a session's progress is applied. */
export interface QuestState {
  questId: string;
  metric: QuestMetric;
  progress: number;
  target: number;
  completed: boolean;
  claimed: boolean;
}

/** Applies a session's progress to one quest, returning the new state. */
export function applyQuestProgress(
  state: QuestState,
  progress: Partial<Record<QuestMetric, number>>,
): QuestState {
  const gained = progress[state.metric] ?? 0;
  if (gained <= 0) return state;
  const next = isHighWater(state.metric)
    ? Math.max(state.progress, gained)
    : state.progress + gained;
  return { ...state, progress: next, completed: state.completed || next >= state.target };
}

/* ------------------------------------------------------ monthly challenge */

export const MONTHLY_GEMS = 200;

/** `YYYY-MM` from a local date. */
export function monthKey(localDate: string): string {
  return localDate.slice(0, 7);
}

/**
 * The month's thirty quests, one a day.
 *
 * Drawn from the same pool with monthly framing: the targets are the hard ones
 * scaled up, because a month is thirty times a day and the challenge should be
 * worth the badge.
 */
export function monthlyChallenge(
  catalog: readonly QuestDef[],
  month: string,
  sectionReached: number,
): IssuedQuest[] {
  const rng = new Rng(dateSeed(month));
  const eligible = catalog.filter((quest) => (quest.minSection ?? 1) <= sectionReached);
  const out: IssuedQuest[] = [];

  for (let day = 0; day < 30; day += 1) {
    const quest = eligible[day % eligible.length];
    if (quest === undefined) break;
    out.push({
      questId: quest.id,
      difficulty: 'hard',
      target: questTarget(quest, 'hard', sectionReached),
      gems: 0,
    });
  }
  return rng.shuffle(out);
}
